import type {
  PrintJobSnapshot,
  PrintJobStatus,
  PrintTargetSnapshot,
  PrintTargetStatus,
  createPrintAuthority,
} from '../printing/print-authority.ts';
import type {
  SmtPrintJobViewModel,
  SmtPrintReprintCommand,
  SmtPrintRetryFailedCommand,
  SmtPrintingMutationContext,
  SmtPrintingMutationResult,
  SmtPrintingRequest,
  SmtPrintingViewModel,
  SmtPrintSemanticState,
  SmtPrintTargetViewModel,
} from './smt-printing-contract.ts';

type PrintAuthority = ReturnType<typeof createPrintAuthority>;

export interface CreateSmtPrintingSurfaceDependencies {
  readonly print: Pick<PrintAuthority, 'listByOrder' | 'createReprint' | 'retryFailed' | 'dispatch'>;
}

const assertRequest = (request: SmtPrintingRequest): void => {
  if (!request.orderId?.trim()) throw new Error('SMT_PRINTING_ORDER_ID_REQUIRED');
  if (!request.actorId?.trim()) throw new Error('SMT_PRINTING_ACTOR_REQUIRED');
  if (!Number.isFinite(Date.parse(request.at))) throw new Error('SMT_PRINTING_TIMESTAMP_INVALID');
};

const assertMutationContext = (context: SmtPrintingMutationContext): void => {
  if (!context.actorId?.trim()) throw new Error('SMT_PRINTING_TRUSTED_ACTOR_REQUIRED');
};

const assertText = (value: string | undefined, code: string): void => {
  if (!value?.trim()) throw new Error(code);
};

const knownMutationErrorCode = (error: unknown): string | null => {
  if (!(error instanceof Error)) return null;
  return error.message.startsWith('PRINT_') || error.message.startsWith('SMT_PRINTING_')
    ? error.message
    : null;
};

const jobSemanticState = (status: PrintJobStatus): SmtPrintSemanticState => {
  switch (status) {
    case 'queued': return 'awaiting_dispatch';
    case 'dispatching': return 'native_execution_in_progress';
    case 'accepted': return 'native_execution_accepted';
    case 'failed': return 'execution_failed';
    case 'unknown': return 'execution_uncertain';
  }
};

const targetSemanticState = (status: PrintTargetStatus): SmtPrintSemanticState => {
  switch (status) {
    case 'pending': return 'awaiting_dispatch';
    case 'dispatching': return 'native_execution_in_progress';
    case 'accepted': return 'native_execution_accepted';
    case 'failed': return 'execution_failed';
    case 'unknown': return 'execution_uncertain';
  }
};

const projectTarget = (target: PrintTargetSnapshot): SmtPrintTargetViewModel => ({
  role: target.role,
  copies: target.copies,
  status: target.status,
  semanticState: targetSemanticState(target.status),
  physicalPrintConfirmed: false,
  ...(target.lastError ? { lastError: target.lastError } : {}),
});

const projectJob = (job: PrintJobSnapshot): SmtPrintJobViewModel => ({
  intentId: job.intentId,
  ...(job.orderId !== undefined ? { orderId: job.orderId } : {}),
  ...(job.orderRevision !== undefined ? { orderRevision: job.orderRevision } : {}),
  ...(job.documentRef ? { documentRef: structuredClone(job.documentRef) } : {}),
  dedupeKey: job.dedupeKey,
  documentKind: job.documentKind,
  templateVersion: job.templateVersion,
  payloadVersion: job.payloadVersion,
  requestedBy: job.requestedBy,
  ...(job.reason ? { reason: job.reason } : {}),
  ...(job.reprintOfIntentId ? { reprintOfIntentId: job.reprintOfIntentId } : {}),
  status: job.status,
  semanticState: jobSemanticState(job.status),
  physicalPrintConfirmed: false,
  attempts: job.attempts,
  retryHistory: structuredClone(job.retryHistory),
  canRetryFailed: job.status === 'failed',
  canExplicitReprint: true,
  requiresManualAttention: job.status === 'unknown',
  targets: job.targets.map(projectTarget),
});

const projectView = (orderId: string, jobs: readonly PrintJobSnapshot[]): SmtPrintingViewModel => ({
  orderId,
  jobs: jobs.map(projectJob),
});

export function createSmtPrintingSurface(deps: CreateSmtPrintingSurfaceDependencies) {
  const snapshot = async (request: SmtPrintingRequest): Promise<SmtPrintingViewModel> => {
    assertRequest(request);
    const jobs = await deps.print.listByOrder(request.orderId);
    return projectView(request.orderId, jobs);
  };

  const mutationSnapshot = async (
    command: SmtPrintingRequest,
    context: SmtPrintingMutationContext,
  ): Promise<SmtPrintingViewModel> => snapshot({
    orderId: command.orderId,
    actorId: context.actorId,
    at: command.at,
  });

  const rejectMutation = async (
    command: SmtPrintingRequest,
    context: SmtPrintingMutationContext,
    code: string,
  ): Promise<SmtPrintingMutationResult> => ({
    kind: 'rejected',
    code,
    snapshot: await mutationSnapshot(command, context),
  });

  const reprint = async (
    command: SmtPrintReprintCommand,
    context: SmtPrintingMutationContext,
  ): Promise<SmtPrintingMutationResult> => {
    assertRequest(command);
    assertMutationContext(context);
    if (command.actorId !== context.actorId) {
      return rejectMutation(command, context, 'SMT_PRINTING_ACTOR_MISMATCH');
    }

    try {
      assertText(command.sourceIntentId, 'SMT_PRINTING_SOURCE_INTENT_ID_REQUIRED');
      assertText(command.newIntentId, 'SMT_PRINTING_NEW_INTENT_ID_REQUIRED');
      assertText(command.reason, 'SMT_PRINTING_REPRINT_REASON_REQUIRED');

      const current = await deps.print.listByOrder(command.orderId);
      if (!current.some((job) => job.intentId === command.sourceIntentId)) {
        return rejectMutation(command, context, 'SMT_PRINTING_INTENT_NOT_IN_ORDER');
      }

      deps.print.createReprint(command.sourceIntentId, {
        intentId: command.newIntentId,
        requestedBy: context.actorId,
        reason: command.reason,
      });
      await deps.print.dispatch(command.newIntentId);
    } catch (error: unknown) {
      const code = knownMutationErrorCode(error);
      if (!code) throw error;
      return rejectMutation(command, context, code);
    }

    return {
      kind: 'accepted',
      snapshot: await mutationSnapshot(command, context),
    };
  };

  const retryFailed = async (
    command: SmtPrintRetryFailedCommand,
    context: SmtPrintingMutationContext,
  ): Promise<SmtPrintingMutationResult> => {
    assertRequest(command);
    assertMutationContext(context);
    if (command.actorId !== context.actorId) {
      return rejectMutation(command, context, 'SMT_PRINTING_ACTOR_MISMATCH');
    }

    try {
      assertText(command.intentId, 'SMT_PRINTING_INTENT_ID_REQUIRED');
      assertText(command.reason, 'SMT_PRINTING_RETRY_REASON_REQUIRED');

      const current = await deps.print.listByOrder(command.orderId);
      if (!current.some((job) => job.intentId === command.intentId)) {
        return rejectMutation(command, context, 'SMT_PRINTING_INTENT_NOT_IN_ORDER');
      }

      await deps.print.retryFailed(command.intentId, {
        requestedBy: context.actorId,
        reason: command.reason,
        at: command.at,
      });
      await deps.print.dispatch(command.intentId);
    } catch (error: unknown) {
      const code = knownMutationErrorCode(error);
      if (!code) throw error;
      return rejectMutation(command, context, code);
    }

    return {
      kind: 'accepted',
      snapshot: await mutationSnapshot(command, context),
    };
  };

  return { snapshot, reprint, retryFailed };
}
